import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useOabCaptureAnswer } from '../../modules/OabCapture/useOabCaptureAnswer';
import { useNewOnboardingEligibility } from '../../hooks/useNewOnboardingEligibility';
import { useOabEligibility } from '../../services/oabEligibility';
import { trackOabCaptureSurfaceViewed } from '../../services/tracking/oabCapture/oabCaptureEvents';
import { ROUTES_PATH } from '../../constants/Routes';
import { OnboardingWelcomeStatus } from './types';

const SURFACE = 'onboarding_welcome';

export function useOnboardingWelcome() {
  const history = useHistory();
  const { pathname } = useLocation();
  const {
    baseEligible,
    isSubscriptionLoading,
    isStudentLoading,
    flagResolved,
    isPreferencesLoading,
    isPollingCredits,
  } = useNewOnboardingEligibility('onboardingOabCompleted');
  const { profession, isError, isEligibilityResolved, answerProfession } =
    useOabEligibility({ enabled: baseEligible });

  const status = ((): OnboardingWelcomeStatus => {
    if (
      isStudentLoading ||
      isSubscriptionLoading ||
      !flagResolved ||
      isPreferencesLoading
    ) {
      return 'loading';
    }

    if (!baseEligible) {
      return 'ineligible';
    }

    if (!isEligibilityResolved) {
      return 'loading';
    }

    if (profession != null) {
      return 'completed';
    }

    // Fail open: a failed lookup must not trap the user on the question.
    return isError ? 'ineligible' : 'ready';
  })();

  const onAnswered = useCallback(
    (isLawyer: boolean) =>
      history.push(
        isLawyer ? ROUTES_PATH.ONBOARDING.OAB : ROUTES_PATH.ONBOARDING.OFFICE,
      ),
    [history],
  );

  const { answer, hasAnswerFailed } = useOabCaptureAnswer({
    surface: SURFACE,
    answerProfession,
    onAnswered,
  });

  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (
      pathname !== ROUTES_PATH.ONBOARDING.WELCOME ||
      status !== 'ready' ||
      hasTrackedViewRef.current
    ) {
      return;
    }

    hasTrackedViewRef.current = true;
    trackOabCaptureSurfaceViewed({ surface: SURFACE, step: 'question' });
  }, [pathname, status]);

  return { status, isPollingCredits, answer, hasAnswerFailed };
}
